
import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { AnalogHorrorPopup } from './AnalogHorror/AnalogHorrorPopup';
import { useAnalogHorrorProvider } from './AnalogHorror/useAnalogHorrorProvider';
import { PopupType } from './AnalogHorror/types';
import { hasShownAchievementPopup, markAchievementPopupAsShown } from '@/components/achievements/utils/achievementUtils';
import { useAuth } from '@/components/AuthProvider';
import { useAIPersonality } from '@/hooks/use-ai-personality';
import { EngagementMonitor } from './EngagementMonitor';

interface AnalogHorrorContextType {
  showMessage: (message: string, type?: PopupType) => void;
  aiPersonality: string;
}

const AnalogHorrorContext = createContext<AnalogHorrorContextType | null>(null);

export const useAnalogHorror = () => {
  const context = useContext(AnalogHorrorContext);
  if (!context) {
    throw new Error('useAnalogHorror must be used within an AnalogHorrorProvider');
  }
  return context;
};

interface AnalogHorrorProviderProps {
  children: ReactNode;
}

export const AnalogHorrorProvider = ({ children }: AnalogHorrorProviderProps) => {
  // Create a safe implementation of the provider that won't crash if React Query context is not available
  const [aiPersonality, setAIPersonality] = useState<string>('ultraterrestrial');
  const [isProviderReady, setIsProviderReady] = useState(false);
  const [tempMessage, setTempMessage] = useState<{message: string, type: PopupType} | null>(null);

  // Create a fallback implementation for when the hook can't be used
  const defaultShowMessage = (message: string) => {
    console.log('AnalogHorror message (fallback):', message);
  };

  // Listen for level-up events from GamificationEffectsProvider
  useEffect(() => {
    const handleLevelUp = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.message) {
        setTempMessage({
          message: customEvent.detail.message,
          type: "level"
        });
      }
    };

    window.addEventListener("level-up-achieved", handleLevelUp);
    return () => {
      window.removeEventListener("level-up-achieved", handleLevelUp);
    };
  }, []);

  // Try to initialize the real provider functionality
  try {
    const { isOpen, message, messageType, showMessage: showProviderMessage, handleClose } = useAnalogHorrorProvider();
    
    // Use our new custom hook to get the AI personality consistently
    const personalityHook = useAIPersonality();
    // Use a valid AIPersonality value as fallback instead of "default"
    const personalityValue = personalityHook?.aiPersonality || 'ultraterrestrial';
    
    // Set the AI personality from the hook if available
    if (personalityValue !== aiPersonality) {
      setAIPersonality(personalityValue);
    }

    if (!isProviderReady) {
      setIsProviderReady(true);
    }

    // If there's a temp message from the event listener, show it
    useEffect(() => {
      if (tempMessage) {
        showProviderMessage(tempMessage.message, tempMessage.type);
        setTempMessage(null);
      }
    }, [tempMessage, showProviderMessage]);

    // Create a modified showMessage that checks for achievement popups
    const showMessage = (message: string, type: PopupType = "success") => {
      // If this is an achievement message, check if we've already shown it
      if (type === "achievement") {
        // Extract the achievement name from the message
        const match = message.match(/ACHIEVEMENT UNLOCKED: ([^.]+)/);
        if (match && match[1]) {
          const achievementName = match[1].trim();
          // Create a unique identifier for this achievement message
          const achievementId = `achievement-${achievementName}`;
          
          // Check if we've already shown a popup for this achievement
          if (hasShownAchievementPopup(achievementId)) {
            return; // Skip showing the popup
          }
          
          // Mark this achievement as having shown a popup
          markAchievementPopupAsShown(achievementId);
        }
      }
      
      // For non-achievement messages or new achievements, show the popup
      showProviderMessage(message, type);
    };

    return (
      <AnalogHorrorContext.Provider value={{ showMessage, aiPersonality: personalityValue }}>
        {children}
        
        <AnalogHorrorPopup
          isOpen={isOpen}
          onClose={handleClose}
          message={message}
          type={messageType}
          aiPersonality={personalityValue}
        />
        
        <EngagementMonitor />
      </AnalogHorrorContext.Provider>
    );
  } catch (error) {
    // Fallback in case of QueryClient context error
    console.error("Error in AnalogHorrorProvider:", error);
    
    // Return a simplified provider with default behavior
    // Use a valid AIPersonality value as fallback
    return (
      <AnalogHorrorContext.Provider value={{ 
        showMessage: defaultShowMessage, 
        aiPersonality: aiPersonality
      }}>
        {children}
      </AnalogHorrorContext.Provider>
    );
  }
};
